import { useServices } from '@/app/providers';
import { useRoleLabel } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { LogOut, MoreHorizontal, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@yawi/infrastructure';

interface UserDropdownProps {
  collapsed?: boolean;
}

export function UserDropdown({ collapsed }: UserDropdownProps = {}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { currentUser, logout } = useServices();
  const { t } = useI18n();
  const { getLabel } = useRoleLabel();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!currentUser) return null;

  const avatar = currentUser.avatarUrl ? (
    <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-theme-light flex-shrink-0">
      <img src={currentUser.avatarUrl} alt={currentUser.name} className="w-full h-full object-cover" />
    </div>
  ) : (
    <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
      {getInitials(currentUser.name)}
    </div>
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title={collapsed ? currentUser.name : undefined}
        className={`w-full flex items-center rounded-xl hover:bg-surface-secondary transition-colors ${
          collapsed ? 'justify-center p-1.5' : 'gap-3 px-3 py-2.5'
        }`}
      >
        {avatar}
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-theme truncate leading-tight">{currentUser.name}</p>
              <p className="text-[11px] text-theme-tertiary leading-tight">
                {getLabel(currentUser.tenantRole || currentUser.role)}
              </p>
            </div>
            <MoreHorizontal size={16} className="text-theme-tertiary flex-shrink-0" />
          </>
        )}
      </button>

      {open && (
        <div className={`absolute bottom-full mb-2 glass-heavy rounded-xl shadow-xl border border-theme-light py-1 z-50 ${
          collapsed ? 'left-full ml-2 w-48' : 'left-0 w-full'
        }`}>
          <button
            onClick={() => { navigate('/perfil'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-theme-secondary hover:bg-surface-secondary hover:text-theme transition-colors"
          >
            <User size={15} />
            {t('menu.v2.myProfile')}
          </button>
          <div className="my-1 border-t border-theme-light" />
          <button
            onClick={() => { logout(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut size={15} />
            {t('menu.v2.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
